/**
 * Promotional Items — the cart page's rules, pure.
 *
 * The design lives in Scanner: `C:\Repos\scanner-dev\Promotional Items.md`
 * (§6.1 the metafield, §6.2 the panel, §6.3 the watcher, §16.5 the theme rules).
 * Scanner writes the shop metafield `custom.promotions`; the cart page reads it
 * from a data island. Nothing here touches the DOM, the network or the clock —
 * `now` is always passed in — so every decision is pinned by a unit test.
 *
 * What a free item IS on the storefront: a $0 "placeholder" variant whose SKU is
 * `PROMO-<KEY>-<COLOUR>-<SIZE>` (e.g. `PROMO-NLFREE-BLK-ALG`). In Scanner that
 * SKU is a kit whose recipe is the real shirt.
 *
 * ⚠ The code is a cart-page KEY, not an enforcement (owner, 2026-09-22). The
 *   Scanner gate is what enforces eligibility; the watcher here only keeps an
 *   honest cart honest.
 * ⚠ The panel OFFERS only a row in effect; the watcher KEEPS a line until its
 *   row's `honouredUntil` (a black shirt added Sunday is still black at
 *   Monday's checkout).
 */

import type { CartItem } from "../types/cart.js";
import { isFeeLine } from "./cartSync/canonical.js";

/** The metafield's shape version this code understands. */
export const PROMOTION_METAFIELD_VERSION = 1;

/** Every placeholder SKU starts with this. */
export const PROMOTION_SKU_PREFIX = "PROMO-";

/**
 * The Shopify product type every placeholder carries (Scanner's colour setup sets
 * it). ⚠ The SKU shape alone is NOT enough to call a cart line a free item: a
 * real brand + style could spell `PROMO-…` (brands `P` / `PR` exist and styles
 * may hold hyphens), and the watcher would delete a paid line. Both must hold.
 */
export const PROMOTION_PRODUCT_TYPE = "Promotion Placeholder";

/** A line with no `_warehouse_code` is a Baton Rouge line (the house default). */
const DEFAULT_WAREHOUSE_CODE = "WPBR";

export interface PromotionSize {
  readonly size: string;
  readonly label: string;
}

export interface PromotionEntry {
  readonly id: number;
  readonly codeSha256: string;
  readonly label: string;
  readonly effectiveFrom: string;
  readonly effectiveTo: string | null;
  readonly honouredUntil: string | null;
  readonly placeholderProductHandle: string;
  readonly placeholderSkuPrefix: string;
  readonly warehouseCode: string;
  readonly colorName: string;
  readonly imageUrl: string | null;
  readonly sizes: readonly PromotionSize[];
  readonly defaultSize: string;
  readonly rules: {
    readonly qualifyingVendor: string | null;
    readonly qualifyingWarehouse: string | null;
    readonly maxQtyPerOrder: number;
  };
}

export interface PromotionMetafield {
  readonly version: number;
  readonly generatedAt: string;
  /** Rows carried for the dev theme only; the panel shows them to an opted-in tester. */
  readonly preview: boolean;
  readonly promotions: readonly PromotionEntry[];
}

/** One placeholder variant, as Liquid rendered it for the buyer (null product ⇒ not visible to them). */
export interface PromotionVariantInfo {
  readonly id: number;
  readonly sku: string;
  readonly available: boolean;
  readonly inventory: number;
}

export interface PromotionProductInfo {
  readonly handle: string;
  readonly variants: readonly PromotionVariantInfo[];
}

// ── Parsing ──────────────────────────────────────────────────────────────────

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isString(value: unknown): value is string {
  return typeof value === "string";
}

function isStringOrNull(value: unknown): value is string | null {
  return value === null || typeof value === "string";
}

function isEntry(value: unknown): value is PromotionEntry {
  if (!isRecord(value)) return false;
  const rules = value.rules;
  return typeof value.id === "number"
    && isString(value.codeSha256)
    && isString(value.label)
    && isString(value.effectiveFrom)
    && isStringOrNull(value.effectiveTo)
    && isStringOrNull(value.honouredUntil)
    && isString(value.placeholderProductHandle)
    && isString(value.placeholderSkuPrefix)
    && isString(value.warehouseCode)
    && isString(value.colorName)
    && isStringOrNull(value.imageUrl)
    && Array.isArray(value.sizes)
    && value.sizes.every((s: unknown) => isRecord(s) && isString(s.size) && isString(s.label))
    && isString(value.defaultSize)
    && isRecord(rules)
    && isStringOrNull(rules.qualifyingVendor)
    && isStringOrNull(rules.qualifyingWarehouse)
    && typeof rules.maxQtyPerOrder === "number";
}

/**
 * The metafield, validated. Null when it is absent, malformed, or a version this
 * code does not know — the panel then hides, and the watcher treats every free
 * item as no longer honoured.
 */
export function parsePromotionMetafield(raw: unknown): PromotionMetafield | null {
  if (!isRecord(raw)) return null;
  if (raw.version !== PROMOTION_METAFIELD_VERSION) return null;
  if (!Array.isArray(raw.promotions)) return null;
  const promotions = raw.promotions.filter(isEntry);
  if (promotions.length !== raw.promotions.length) return null;
  return {
    version: PROMOTION_METAFIELD_VERSION,
    generatedAt: isString(raw.generatedAt) ? raw.generatedAt : "",
    preview: raw.preview === true,
    promotions,
  };
}

/** The Liquid-rendered placeholder products, validated; anything malformed is dropped. */
export function parsePromotionProducts(raw: unknown): PromotionProductInfo[] {
  if (!Array.isArray(raw)) return [];
  const out: PromotionProductInfo[] = [];
  for (const p of raw) {
    if (!isRecord(p) || !isString(p.handle) || !Array.isArray(p.variants)) continue;
    const variants = p.variants.filter((v: unknown): v is PromotionVariantInfo =>
      isRecord(v) && typeof v.id === "number" && isString(v.sku) && typeof v.available === "boolean" && typeof v.inventory === "number");
    out.push({ handle: p.handle, variants });
  }
  return out;
}

// ── Placeholder SKUs ─────────────────────────────────────────────────────────

function upper(value: string | null | undefined): string {
  return (value ?? "").trim().toUpperCase();
}

export function isPromotionSku(sku: string | null | undefined): boolean {
  const s = upper(sku);
  return s.length > PROMOTION_SKU_PREFIX.length && s.startsWith(PROMOTION_SKU_PREFIX);
}

/** Is this cart line a free promotion item? The placeholder product type AND the SKU shape. */
export function isPromotionLine(item: Pick<CartItem, "sku" | "product_type">): boolean {
  return item.product_type === PROMOTION_PRODUCT_TYPE && isPromotionSku(item.sku);
}

/** `PROMO-NLFREE-BLK-ALG` → prefix `PROMO-NLFREE-BLK`, size `ALG`, key `NLFREE`. Null when not that shape. */
export function parsePromotionSku(sku: string | null | undefined): { prefix: string; size: string; key: string } | null {
  if (!isPromotionSku(sku)) return null;
  const s = upper(sku);
  const last = s.lastIndexOf("-");
  const prefix = s.slice(0, last);
  const size = s.slice(last + 1);
  const match = /^PROMO-([A-Z0-9]+)-[A-Z0-9+]+$/.exec(prefix);
  if (!match || size.length === 0) return null;
  return { prefix, size, key: match[1] };
}

/** The key a row belongs to — its prefix's `<KEY>`. */
function keyOfPrefix(prefix: string): string | null {
  return /^PROMO-([A-Z0-9]+)-/.exec(upper(prefix))?.[1] ?? null;
}

/** The normalized code the metafield's `codeSha256` is the hash of. */
export function normalizePromotionCode(code: string): string {
  return code.trim().toUpperCase();
}

// ── Time ─────────────────────────────────────────────────────────────────────

function at(value: string | null): number | null {
  if (value === null) return null;
  const t = Date.parse(value);
  return Number.isNaN(t) ? null : t;
}

/** In effect: started and not ended. The panel offers only these. */
export function isRowInEffect(row: PromotionEntry, now: number): boolean {
  const from = at(row.effectiveFrom);
  if (from === null || now < from) return false;
  const to = at(row.effectiveTo);
  return row.effectiveTo === null || (to !== null && now < to);
}

/** Honoured: started, and before its grace ends. The watcher keeps a line while this holds. */
export function isRowHonoured(row: PromotionEntry, now: number): boolean {
  const from = at(row.effectiveFrom);
  if (from === null || now < from) return false;
  const until = at(row.honouredUntil);
  return row.honouredUntil === null || (until !== null && now < until);
}

// ── Qualification ────────────────────────────────────────────────────────────

function warehouseOf(item: CartItem): string {
  return upper(item.properties?._warehouse_code) || DEFAULT_WAREHOUSE_CODE;
}

/** A PAID line: money on it, not a free item, not a fee. */
function isPaidLine(item: CartItem): boolean {
  return (item.final_line_price ?? (item.price ?? 0) * item.quantity) > 0 && !isPromotionLine(item) && !isFeeLine(item);
}

export interface Qualification {
  readonly qualifies: boolean;
  /** What is missing, in the customer's words. Empty when it qualifies. */
  readonly missing: readonly string[];
}

/**
 * Does the cart earn the row's free item? Paid lines only: one of the qualifying
 * vendor, and one shipping from the qualifying warehouse (not necessarily the
 * same line — the rule is two conditions, as in Scanner's gate).
 */
export function qualifies(items: readonly CartItem[], row: PromotionEntry): Qualification {
  const paid = items.filter(isPaidLine);
  const missing: string[] = [];
  const vendor = row.rules.qualifyingVendor;
  if (vendor !== null && !paid.some((i) => upper(i.vendor) === upper(vendor))) {
    missing.push(`Add at least one ${vendor} item to your cart.`);
  }
  const warehouse = row.rules.qualifyingWarehouse;
  if (warehouse !== null && !paid.some((i) => warehouseOf(i) === upper(warehouse))) {
    missing.push(`Add at least one item shipping from ${warehouse === DEFAULT_WAREHOUSE_CODE ? "our Baton Rouge warehouse" : warehouse}.`);
  }
  return { qualifies: missing.length === 0, missing };
}

// ── The panel ────────────────────────────────────────────────────────────────

/**
 * Does the cart page show the promotion box at all? (owner, 2026-09-27: only when
 * the promotion can apply, and not once it has been applied.) Yes only when the
 * cart earns a row in effect (`qualifies`) whose placeholder this buyer can see (it
 * is in `products`) AND the cart holds no free item of that row's key yet.
 *   - A cart with nothing that earns one shows no box, so a customer is never
 *     offered a code they cannot use.
 *   - A cart that already holds its free item shows no box either: there is no
 *     change-size control. The customer deletes the free line (its trash button)
 *     and adds it again, and the box comes back the moment the line goes.
 * The page re-asks after every change it makes without a reload (the trash button,
 * a cart-sync apply).
 * ⚠ A preview row counts only for a tester who opted in (`previewAllowed`).
 */
export function shouldShowPromotionPanel(
  items: readonly CartItem[],
  metafield: PromotionMetafield | null,
  products: readonly PromotionProductInfo[],
  now: number,
  previewAllowed: boolean,
): boolean {
  if (metafield === null) return false;
  if (metafield.preview && !previewAllowed) return false;
  const heldKeys = new Set<string>();
  for (const item of items) {
    const parsed = isPromotionLine(item) ? parsePromotionSku(item.sku) : null;
    if (parsed !== null) heldKeys.add(parsed.key);
  }
  return metafield.promotions.some((r) => isRowInEffect(r, now)
    && !heldKeys.has(keyOfPrefix(r.placeholderSkuPrefix) ?? "")
    && products.some((p) => p.handle === r.placeholderProductHandle)
    && qualifies(items, r).qualifies);
}

/** The row a typed code unlocks right now, or null. */
export function rowForCode(metafield: PromotionMetafield, codeSha256: string, now: number): PromotionEntry | null {
  const hash = codeSha256.trim().toLowerCase();
  const matches = metafield.promotions.filter((r) => r.codeSha256.toLowerCase() === hash && isRowInEffect(r, now));
  return matches.sort((a, b) => (at(b.effectiveFrom) ?? 0) - (at(a.effectiveFrom) ?? 0))[0] ?? null;
}

export interface PromotionSizeChoice {
  readonly size: string;
  readonly label: string;
  readonly variantId: number | null;
  /** False ⇒ shown but disabled (sold out, or not visible to this buyer). */
  readonly selectable: boolean;
}

/** The row's sizes, in its order, each with the buyer's variant and whether it can be chosen. */
export function sizeChoices(row: PromotionEntry, product: PromotionProductInfo | null): PromotionSizeChoice[] {
  return row.sizes.map((s) => {
    const sku = `${upper(row.placeholderSkuPrefix)}-${upper(s.size)}`;
    const v = product?.variants.find((x) => upper(x.sku) === sku) ?? null;
    return { size: upper(s.size), label: s.label, variantId: v?.id ?? null, selectable: v !== null && v.available && v.inventory > 0 };
  });
}

/** The cart operations the panel issues — plain data; the caller turns them into a `CartDiffResult`. */
export interface PromotionCartPlan {
  /** Lines to set to zero, by line key. */
  readonly remove: ReadonlyArray<{ readonly lineKey: string; readonly variantId: number; readonly warehouseCode: string; readonly quantity: number }>;
  /** Lines to set to a quantity, by line key. */
  readonly setQuantity: ReadonlyArray<{ readonly lineKey: string; readonly variantId: number; readonly warehouseCode: string; readonly from: number; readonly to: number }>;
  /** New lines. */
  readonly add: ReadonlyArray<{ readonly variantId: number; readonly warehouseCode: string; readonly quantity: number }>;
}

export function isEmptyPlan(plan: PromotionCartPlan): boolean {
  return plan.remove.length === 0 && plan.setQuantity.length === 0 && plan.add.length === 0;
}

/**
 * Put exactly ONE of the chosen size in the cart, and nothing else of this
 * promotion's key — REPLACE, never add a second (§16.5): a size change, or next
 * week's colour, swaps the line rather than stacking one. Idempotent: planned
 * against a cart that already holds exactly that, it is empty.
 */
export function planAddFreeItem(items: readonly CartItem[], row: PromotionEntry, variantId: number): PromotionCartPlan {
  const key = keyOfPrefix(row.placeholderSkuPrefix);
  const warehouseCode = upper(row.warehouseCode);
  const sameKey = items.filter((i) => {
    const parsed = isPromotionLine(i) ? parsePromotionSku(i.sku) : null;
    return parsed !== null && parsed.key === key;
  });
  const target = sameKey.find((i) => i.variant_id === variantId && warehouseOf(i) === warehouseCode) ?? null;
  const remove = sameKey
    .filter((i) => i !== target)
    .map((i) => ({ lineKey: i.key, variantId: i.variant_id, warehouseCode: warehouseOf(i), quantity: i.quantity }));
  const setQuantity = target && target.quantity !== 1
    ? [{ lineKey: target.key, variantId, warehouseCode, from: target.quantity, to: 1 }]
    : [];
  const add = target ? [] : [{ variantId, warehouseCode, quantity: 1 }];
  return { remove, setQuantity, add };
}

// ── The watcher ──────────────────────────────────────────────────────────────

/** Why the watcher changed a line — shown to the customer after the page reloads. */
export const WatcherReason = {
  Unavailable: "unavailable",
  Ended: "ended",
  SizeNotOffered: "size",
  WrongWarehouse: "warehouse",
  NoLongerQualifies: "qualify",
  OverLimit: "limit",
} as const;
export type WatcherReason = (typeof WatcherReason)[keyof typeof WatcherReason];

export interface WatcherPlan extends PromotionCartPlan {
  /** One entry per line changed, with the reason — the customer is told. */
  readonly reasons: ReadonlyArray<{ readonly lineKey: string; readonly sku: string; readonly reason: WatcherReason }>;
}

/** The row governing a line now: a row in effect first, then one in its grace. */
function governingRow(metafield: PromotionMetafield, prefix: string, now: number): PromotionEntry | null {
  const rows = metafield.promotions.filter((r) => upper(r.placeholderSkuPrefix) === prefix && isRowHonoured(r, now));
  const byStart = (a: PromotionEntry, b: PromotionEntry): number => (at(b.effectiveFrom) ?? 0) - (at(a.effectiveFrom) ?? 0);
  return rows.filter((r) => isRowInEffect(r, now)).sort(byStart)[0] ?? rows.sort(byStart)[0] ?? null;
}

/**
 * What the watcher removes or trims (§6.3). It runs whenever the cart holds a
 * free item, WHATEVER the metafield says — a null metafield (absent, malformed,
 * unknown version) removes every free item.
 *
 * A line goes when its row is no longer honoured, its size is not offered, it
 * sits on another warehouse, or the cart no longer qualifies. Then the sum
 * across sizes AND colours of a key is capped at the row's limit, keeping first
 * the colour in effect, then the newest line (`/cart/add.js` appends, so a
 * higher index is newer).
 */
export function planWatcher(items: readonly CartItem[], metafield: PromotionMetafield | null, now: number): WatcherPlan {
  const remove: Array<PromotionCartPlan["remove"][number]> = [];
  const setQuantity: Array<PromotionCartPlan["setQuantity"][number]> = [];
  const reasons: Array<WatcherPlan["reasons"][number]> = [];
  const survivors: Array<{ item: CartItem; index: number; key: string; row: PromotionEntry }> = [];

  items.forEach((item, index) => {
    const parsed = parsePromotionSku(item.sku);
    if (!isPromotionLine(item)) return;
    const drop = (reason: WatcherReason): void => {
      remove.push({ lineKey: item.key, variantId: item.variant_id, warehouseCode: warehouseOf(item), quantity: item.quantity });
      reasons.push({ lineKey: item.key, sku: upper(item.sku), reason });
    };
    if (metafield === null || parsed === null) { drop(WatcherReason.Unavailable); return; }
    const row = governingRow(metafield, parsed.prefix, now);
    if (row === null) { drop(WatcherReason.Ended); return; }
    if (!row.sizes.some((s) => upper(s.size) === parsed.size)) { drop(WatcherReason.SizeNotOffered); return; }
    if (warehouseOf(item) !== upper(row.warehouseCode)) { drop(WatcherReason.WrongWarehouse); return; }
    if (!qualifies(items, row).qualifies) { drop(WatcherReason.NoLongerQualifies); return; }
    survivors.push({ item, index, key: parsed.key, row });
  });

  // The cap, per key, across sizes and colours.
  const byKey = new Map<string, typeof survivors>();
  for (const s of survivors) byKey.set(s.key, [...(byKey.get(s.key) ?? []), s]);
  for (const group of byKey.values()) {
    const limit = Math.max(0, Math.min(...group.map((g) => g.row.rules.maxQtyPerOrder)));
    const ordered = [...group].sort((a, b) => {
      const inEffect = Number(isRowInEffect(b.row, now)) - Number(isRowInEffect(a.row, now));
      return inEffect !== 0 ? inEffect : b.index - a.index;
    });
    let left = limit;
    for (const g of ordered) {
      const keep = Math.min(left, g.item.quantity);
      left -= keep;
      if (keep === g.item.quantity) continue;
      if (keep === 0) {
        remove.push({ lineKey: g.item.key, variantId: g.item.variant_id, warehouseCode: warehouseOf(g.item), quantity: g.item.quantity });
      } else {
        setQuantity.push({ lineKey: g.item.key, variantId: g.item.variant_id, warehouseCode: warehouseOf(g.item), from: g.item.quantity, to: keep });
      }
      reasons.push({ lineKey: g.item.key, sku: upper(g.item.sku), reason: WatcherReason.OverLimit });
    }
  }
  return { remove, setQuantity, add: [], reasons };
}

/** The customer-facing sentence for a watcher change. */
export function describeWatcherReason(reason: WatcherReason): string {
  switch (reason) {
    case WatcherReason.Unavailable:
      return "the promotion is not available right now";
    case WatcherReason.Ended:
      return "the promotion has ended";
    case WatcherReason.SizeNotOffered:
      return "that size is not part of the promotion";
    case WatcherReason.WrongWarehouse:
      return "it must ship from the promotion's warehouse";
    case WatcherReason.NoLongerQualifies:
      return "your cart no longer qualifies";
    case WatcherReason.OverLimit:
      return "the promotion allows a limited number per order";
  }
}
